import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  CheckCircle,
  FileText,
  Send,
  Users,
  GraduationCap,
  MapPin,
  Phone,
  Mail,
} from 'lucide-react'
import Button from '../ui/Button'
import { applicationsApi } from '../../services/api'
import { DEFAULT_CONTACT } from '../../constants/contact'
import { fadeInUp, viewportOnce } from '../../utils/animations'

const STEPS = [
  { key: 'step1', icon: FileText, num: '01' },
  { key: 'step2', icon: Users, num: '02' },
  { key: 'step3', icon: CheckCircle, num: '03' },
]

const INITIAL_FORM = {
  parent_name: '',
  email: '',
  phone: '',
  student_name: '',
  grade_applying: '',
  program: '',
  campus: '',
  current_school: '',
  message: '',
}

const inputClass =
  'w-full rounded-xl border border-border bg-background px-4 py-3 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'

export default function ApplyForm() {
  const { t } = useTranslation()
  const [form, setForm] = useState(INITIAL_FORM)
  const [status, setStatus] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setStatus(null)
    try {
      await applicationsApi.submit(form)
      setStatus('success')
      setForm(INITIAL_FORM)
    } catch {
      setStatus('error')
    } finally {
      setSubmitting(false)
    }
  }

  const grades = t('apply.grades', { returnObjects: true })
  const gradeOptions = Object.entries(grades)

  return (
    <div className="grid gap-10 lg:grid-cols-5 lg:gap-12">
      <motion.aside
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeInUp}
        className="lg:col-span-2"
      >
        <div className="rounded-3xl gradient-brand p-8 text-white shadow-2xl shadow-primary/30">
          <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-accent">
            {t('apply.processLabel')}
          </p>
          <h2 className="mb-6 text-2xl font-bold">{t('apply.processTitle')}</h2>

          <ol className="space-y-6">
            {STEPS.map(({ key, icon: Icon, num }) => (
              <li key={key} className="flex gap-4">
                <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15">
                  <Icon className="h-5 w-5" aria-hidden />
                  <span className="absolute -top-1.5 -end-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-[10px] font-bold">
                    {num}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold">{t(`admissions.${key}`)}</h3>
                  <p className="mt-1 text-sm text-white/75">{t(`admissions.${key}Desc`)}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-8 space-y-3 border-t border-white/15 pt-6 text-sm">
            <p className="flex items-center gap-2 text-white/85">
              <Phone className="h-4 w-4 shrink-0 text-accent" aria-hidden />
              <a href={`tel:${DEFAULT_CONTACT.phone}`} className="hover:text-accent">
                {DEFAULT_CONTACT.phone}
              </a>
            </p>
            <p className="flex items-center gap-2 text-white/85">
              <Mail className="h-4 w-4 shrink-0 text-accent" aria-hidden />
              <a href={`mailto:${DEFAULT_CONTACT.email}`} className="hover:text-accent">
                {DEFAULT_CONTACT.email}
              </a>
            </p>
            <p className="flex items-start gap-2 text-white/75">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
              {DEFAULT_CONTACT.address_en}
            </p>
          </div>
        </div>
      </motion.aside>

      <motion.form
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeInUp}
        onSubmit={handleSubmit}
        className="lg:col-span-3 rounded-3xl border border-border bg-white p-6 shadow-xl shadow-primary/5 sm:p-8"
      >
        <div className="mb-6 flex items-center gap-3 border-b border-border/60 pb-6">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
            <GraduationCap className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-xl font-bold text-primary">{t('apply.formTitle')}</h2>
            <p className="text-sm text-text/60">{t('apply.formSubtitle')}</p>
          </div>
        </div>

        <fieldset className="mb-8">
          <legend className="mb-4 text-sm font-bold uppercase tracking-wider text-primary">
            {t('apply.sections.parent')}
          </legend>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('apply.fields.parentName')}
              </label>
              <input
                type="text"
                name="parent_name"
                value={form.parent_name}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('apply.fields.email')}
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('apply.fields.phone')}
              </label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="mb-8">
          <legend className="mb-4 text-sm font-bold uppercase tracking-wider text-primary">
            {t('apply.sections.student')}
          </legend>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('apply.fields.studentName')}
              </label>
              <input
                type="text"
                name="student_name"
                value={form.student_name}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('apply.fields.gradeApplying')}
              </label>
              <select
                name="grade_applying"
                value={form.grade_applying}
                onChange={handleChange}
                required
                className={inputClass}
              >
                <option value="">{t('apply.selectGrade')}</option>
                {gradeOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('apply.fields.program')}
              </label>
              <select
                name="program"
                value={form.program}
                onChange={handleChange}
                required
                className={inputClass}
              >
                <option value="">{t('apply.selectProgram')}</option>
                <option value="national">{t('programs.national.title')}</option>
                <option value="british">{t('programs.british.title')}</option>
                <option value="american">{t('programs.american.title')}</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('apply.fields.campus')}
              </label>
              <select
                name="campus"
                value={form.campus}
                onChange={handleChange}
                required
                className={inputClass}
              >
                <option value="">{t('apply.selectCampus')}</option>
                <option value="MisTlaAlAli">{t('apply.campuses.tlaAlAli')}</option>
                <option value="MisKhalda">{t('apply.campuses.khalda')}</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('apply.fields.currentSchool')}
              </label>
              <input
                type="text"
                name="current_school"
                value={form.current_school}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('apply.fields.message')}
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                placeholder={t('apply.fields.messagePlaceholder')}
                className={`${inputClass} resize-none`}
              />
            </div>
          </div>
        </fieldset>

        {status && (
          <p
            className={`mb-4 rounded-xl px-4 py-3 text-sm ${
              status === 'success'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}
          >
            {t(`apply.form.${status}`)}
          </p>
        )}

        <Button type="submit" variant="accent" size="lg" disabled={submitting} className="w-full sm:w-auto">
          <Send className="h-4 w-4" />
          {submitting ? t('common.loading') : t('apply.submit')}
        </Button>
      </motion.form>
    </div>
  )
}
